/**
 * Записи раздела «Планы» в CRM: QR-папка (новая и дописать), отправка
 * заказов в WeChat пачкой, смена плановой даты — с заставами, занятостью и
 * итогом для подвала. Вынесено из `pages/PlansPage.tsx` (нарезка 14.10.26).
 */

import { useMemo, useRef, useState } from "react";
import { ApiFailure, apiFetch } from "@/lib/api";
import {
  bulkConfirm,
  bulkPlan,
  type PlanGroup,
  plural,
  qrFolderWarnings,
  qrProblem,
  type QrFolder,
  supplierName,
} from "@/lib/plans";
import type { PlanRow } from "@/lib/providers";
import {
  MAX_BATCH as MAX_WECHAT_BATCH,
  RATE_PER_HOUR as WECHAT_RATE_PER_HOUR,
  batchNote,
  type BatchItem,
} from "@/lib/wechat";
import type { DateMoved, FiltersPayload, QrCreated, QrNote } from "./types";

export function usePlanActions({
  dictData,
  groups,
  pickedPlans,
  picked,
  pick,
  setPicked,
  refetch,
}: {
  /** Справочники экрана; `undefined` — ещё едут, заставы неизвестны. */
  dictData: FiltersPayload | undefined;
  /** Группы таблицы в её порядке — по ним «QR + WeChat» заводит папки. */
  groups: PlanGroup[];
  pickedPlans: PlanRow[];
  picked: ReadonlySet<number>;
  pick: (planIds: number[], on: boolean) => void;
  setPicked: (next: ReadonlySet<number>) => void;
  /** Перечитать партии — `plans.query.refetch` страницы. */
  refetch: () => Promise<unknown>;
}) {
  // Пока справочники не приехали, застава неизвестна — и кнопка закрыта: открытой
  // она обещала бы запись, которую сервер, возможно, отобьёт 409.
  const qrWhy = qrProblem(
    pickedPlans,
    dictData ? dictData.qr_blocked : "ждём справочники экрана",
  );
  const [qrBusy, setQrBusy] = useState(false);
  const [qrNote, setQrNote] = useState<QrNote | null>(null);

  // `target` — партии группы, когда папку просят кликом по «без QR-папки» на
  // строке группы (разметка 28.09.26 12:59 п.1); без него — отмеченные.
  const createQr = async (target?: PlanRow[]) => {
    const list = target ?? pickedPlans;
    const why = target ? qrProblem(target, dictData ? dictData.qr_blocked : "ждём справочники экрана") : qrWhy;
    const first = list[0];
    if (why || qrBusy || !first) return;
    const ids = list.map((plan) => plan.id);
    // Подтверждение — не церемония: это запись в боевую CRM из отбора, где
    // рядом стоит безобидное «открыть».
    const ask =
      `Создать QR-папку в CRM для ${ids.length} ` +
      `${plural(ids.length, ["партии", "партий", "партий"])} поставщика ${supplierName(first)}?`;
    if (!window.confirm(ask)) return;
    setQrBusy(true);
    setQrNote(null);
    try {
      const done = await apiFetch<QrCreated>("/api/plans/qr", {
        method: "POST",
        body: JSON.stringify({ plan_ids: ids, apply: true }),
      });
      const missed =
        done.unbound.length > 0
          ? ` · номер не лёг партиям ${done.unbound.join(", ")} — проверьте в CRM`
          : "";
      setQrNote({
        text:
          `QR ${done.qr_id} ${done.type === "exist" ? "уже была — привязки обновлены" : "создана"}` +
          ` · привязано ${done.bound} из ${done.plans}${missed}`,
        link: done.link,
        error: done.unbound.length > 0,
      });
      // Выбор снимается, партии перечитываются: группа сама встаёт
      // «поставщик · QR N», и отмеченные строки больше не нужны. Снимаются
      // только партии папки: клик по группе не сбрасывает чужие отметки.
      pick(ids, false);
      void refetch();
    } catch (error) {
      setQrNote({
        text:
          error instanceof ApiFailure
            ? error.message
            : "QR-папка не создана: сервис не ответил",
        error: true,
      });
      // Партии перечитываются и после отказа: 409 значит, что CRM изменилась
      // (партию уже положили в папку), а таймаут мог кончиться созданной папкой.
      // По старым строкам кнопка осталась бы открытой и отбивалась бы снова.
      void refetch();
    } finally {
      setQrBusy(false);
    }
  };

  // ДОПИСАТЬ В СУЩЕСТВУЮЩУЮ ПАПКУ (разметка 28.09.26 11:27 п.1). Сначала сухой
  // прогон: отказ сервер называет словами до записи, а по папке (оплачена,
  // заказ, статус) строится предупреждение — дописывать можно, но закупщик
  // видит последствия (решение владельца 28.09.26).
  const addToQr = async (qrId: number) => {
    if (qrWhy || qrBusy || pickedPlans.length === 0) return;
    const ids = pickedPlans.map((plan) => plan.id);
    setQrBusy(true);
    setQrNote(null);
    try {
      const dry = await apiFetch<{ problems: string[]; folder: QrFolder | null }>("/api/plans/qr", {
        method: "POST",
        body: JSON.stringify({ plan_ids: ids, qr_id: qrId }),
      });
      if (dry.problems.length > 0) {
        setQrNote({ text: `в QR ${qrId} не дописать: ${dry.problems.join("; ")}`, error: true });
        return;
      }
      const warnings = dry.folder ? qrFolderWarnings(dry.folder) : [];
      const ask =
        `Дописать ${ids.length} ${plural(ids.length, ["партию", "партии", "партий"])} в QR ${qrId}?` +
        (warnings.length > 0 ? `\n\nВнимание:\n— ${warnings.join("\n— ")}` : "");
      if (!window.confirm(ask)) return;
      const done = await apiFetch<QrCreated>("/api/plans/qr", {
        method: "POST",
        body: JSON.stringify({ plan_ids: ids, qr_id: qrId, apply: true }),
      });
      const missed =
        done.unbound.length > 0
          ? ` · номер не лёг партиям ${done.unbound.join(", ")} — проверьте в CRM`
          : "";
      setQrNote({
        text: `в QR ${done.qr_id} дописано ${done.bound} из ${done.plans}${missed}`,
        link: done.link,
        error: done.unbound.length > 0,
      });
      setPicked(new Set());
      void refetch();
    } catch (error) {
      setQrNote({
        text: error instanceof ApiFailure ? error.message : `в QR ${qrId} не дописано: сервис не ответил`,
        error: true,
      });
      void refetch();
    } finally {
      setQrBusy(false);
    }
  };

  const createQrRef = useRef(createQr);
  createQrRef.current = createQr;
  const qrBlocked = dictData ? dictData.qr_blocked : "ждём справочники экрана";
  const groupQr = useMemo(
    () => ({
      blocked: qrBlocked,
      busy: qrBusy,
      create: (group: PlanGroup) => void createQrRef.current(group.plans),
    }),
    [qrBlocked, qrBusy],
  );

  // «QR + WeChat» (просьба владельца 23.10.26, спека …-plans-bulk-qr-wechat):
  // группе без папки — своя папка, затем все папки в очередь wechat-api
  // пачками по 20. Уже отправленные сервер пропускает — повтор остаётся
  // кнопкой ↻ у группы, чтобы фабрика не получила заказ дважды. Обрыв
  // посередине безопасен: та же папка вернётся по тому же набору партий, и
  // второй клик доделает остаток.
  const bulk = useMemo(() => bulkPlan(groups, picked, qrBlocked), [groups, picked, qrBlocked]);
  const [wechatBusy, setWechatBusy] = useState(false);
  const [wechatReload, setWechatReload] = useState(0);
  const wechatWhy = bulk.why;

  const sendWechat = async () => {
    if (bulk.why || wechatBusy || qrBusy) return;
    if (!window.confirm(bulkConfirm(bulk, WECHAT_RATE_PER_HOUR))) return;
    setWechatBusy(true);
    setQrNote(null);
    const parts: string[] = [];
    let error = false;
    // Папка → её отмеченные партии: дошедшие до очереди снимаются с отметки.
    const send = new Map(bulk.send.map((s) => [s.qrId, s.planIds]));
    try {
      let created = 0;
      for (const { group, planIds } of bulk.create) {
        try {
          const done = await apiFetch<QrCreated>("/api/plans/qr", {
            method: "POST",
            body: JSON.stringify({ plan_ids: planIds, apply: true }),
          });
          created += 1;
          send.set(done.qr_id, [...(send.get(done.qr_id) ?? []), ...planIds]);
          if (done.unbound.length > 0) {
            error = true;
            parts.push(`QR ${done.qr_id}: номер не лёг партиям ${done.unbound.join(", ")} — проверьте в CRM`);
          }
        } catch (failure) {
          error = true;
          parts.push(
            `${group.supplier}: ${failure instanceof ApiFailure ? failure.message : "папка не создана — сервис не ответил"}`,
          );
        }
      }
      if (bulk.create.length > 0) parts.unshift(`папок создано ${created} из ${bulk.create.length}`);
      for (const s of bulk.skip) parts.push(`${s.group.supplier}: ${s.why}`);
      if (bulk.skip.length > 0) error = true;
      // Пачками по 20 — потолок сервера; темп держит сама очередь wechat-api.
      const qrs = [...send.keys()];
      const items: BatchItem[] = [];
      for (let i = 0; i < qrs.length; i += MAX_WECHAT_BATCH)
        items.push(
          ...(await apiFetch<BatchItem[]>("/api/plans/wechat", {
            method: "POST",
            body: JSON.stringify({ qr: qrs.slice(i, i + MAX_WECHAT_BATCH) }),
          })),
        );
      if (items.length > 0) {
        const note = batchNote(items);
        parts.push(note.text);
        error ||= note.error;
      }
      pick(
        items.filter((item) => item.outcome !== "error").flatMap((item) => send.get(item.qr_id) ?? []),
        false,
      );
    } catch (failure) {
      error = true;
      parts.push(
        failure instanceof ApiFailure ? failure.message : "WeChat: сервис не ответил — проверьте метки групп",
      );
    } finally {
      setQrNote({ text: parts.join(" · "), error });
      setWechatBusy(false);
      // Метки перечитываются и после отказа: часть папок могла уйти до него.
      setWechatReload((n) => n + 1);
      // Новые папки меняют группы: «поставщик · QR N» встаёт после перечитки.
      if (bulk.create.length > 0) void refetch();
    }
  };

  // СМЕНА ПЛАНОВОЙ ДАТЫ ОТМЕЧЕННЫХ ПАРТИЙ (замечание владельца 16.09.26).
  // Причина отказа известна ДО нажатия: `date_blocked` приезжает справочниками
  // и называет либо заставу записи, либо нехватку гранта на журнал партии.
  const dateWhy = dictData
    ? dictData.date_blocked
    : "ждём справочники экрана";
  const [dateOpen, setDateOpen] = useState(false);
  const [dateBusy, setDateBusy] = useState(false);

  const moveDate = async (day: string) => {
    if (dateWhy || dateBusy || picked.size === 0) return;
    const ids = pickedPlans.map((plan) => plan.id);
    // Подтверждение — не церемония: это правка чужой боевой CRM, и рядом
    // стоит безобидное «открыть».
    const ask =
      `Перенести ${ids.length} ${plural(ids.length, ["партию", "партии", "партий"])}` +
      ` на ${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}?`;
    if (!window.confirm(ask)) return;
    setDateOpen(false);
    setDateBusy(true);
    setQrNote(null);
    try {
      const done = await apiFetch<DateMoved>("/api/plans/date", {
        method: "POST",
        body: JSON.stringify({ plan_ids: ids, plan_date: day, apply: true }),
      });
      const skipped =
        done.already > 0 ? ` · ${done.already} уже стояли на этой дате` : "";
      setQrNote({
        text: `перенесено ${done.moved} ${plural(done.moved, ["партия", "партии", "партий"])} на ${done.plan_date}${skipped}`,
        error: false,
      });
      setPicked(new Set());
      void refetch();
    } catch (error) {
      setQrNote({
        text:
          error instanceof ApiFailure
            ? error.message
            : "дата не изменена: сервис не ответил",
        error: true,
      });
      // Партии перечитываются и после отказа: 409 значит, что CRM изменилась
      // под нами, и решать по старым строкам нельзя.
      void refetch();
    } finally {
      setDateBusy(false);
    }
  };

  return {
    qrWhy,
    qrBusy,
    qrNote,
    createQr,
    addToQr,
    groupQr,
    bulk,
    wechatWhy,
    wechatBusy,
    wechatReload,
    sendWechat,
    dateWhy,
    dateOpen,
    setDateOpen,
    dateBusy,
    moveDate,
  };
}
